// ---------------------------------------------------------------------------
// Semantic status color class helpers
//
// All color classes below use CSS custom property tokens (--status-*)
// defined in index.css. These tokens derive their values from the active
// theme's --primary, --destructive, and --muted-foreground variables using
// oklch relative color syntax, so they adapt to every theme and light/dark
// mode automatically.
// ---------------------------------------------------------------------------

export const badgeClass = {
  success: 'bg-status-success/15 text-status-success border-status-success/25',
  error: 'bg-status-error/15 text-status-error border-status-error/25',
  warning: 'bg-status-warning/15 text-status-warning border-status-warning/25',
  info: 'bg-status-info/15 text-status-info border-status-info/25',
  neutral: 'bg-status-neutral/15 text-status-neutral border-status-neutral/25',
  sky: 'bg-status-accent-sky/15 text-status-accent-sky border-status-accent-sky/25',
  cyan: 'bg-status-accent-cyan/15 text-status-accent-cyan border-status-accent-cyan/25',
  teal: 'bg-status-accent-teal/15 text-status-accent-teal border-status-accent-teal/25',
  indigo: 'bg-status-accent-indigo/15 text-status-accent-indigo border-status-accent-indigo/25',
  violet: 'bg-status-accent-violet/15 text-status-accent-violet border-status-accent-violet/25',
  purple: 'bg-status-accent-purple/15 text-status-accent-purple border-status-accent-purple/25',
  orange: 'bg-status-accent-orange/15 text-status-accent-orange border-status-accent-orange/25',
} as const;

export const dotClass = {
  success: 'bg-status-success',
  error: 'bg-status-error',
  warning: 'bg-status-warning',
  info: 'bg-status-info',
  neutral: 'bg-status-neutral',
  sky: 'bg-status-accent-sky',
  cyan: 'bg-status-accent-cyan',
  teal: 'bg-status-accent-teal',
  indigo: 'bg-status-accent-indigo',
  violet: 'bg-status-accent-violet',
  purple: 'bg-status-accent-purple',
  orange: 'bg-status-accent-orange',
} as const;

// ---------------------------------------------------------------------------
// Axon Event Types
//
// Single source of truth: EVENT_COLOR maps event type → semantic color name.
// EVENT_BADGE_CLASS and EVENT_DOT_CLASS are derived automatically — add new
// event types in EVENT_COLOR only.
// ---------------------------------------------------------------------------

type SemanticColor = keyof typeof badgeClass;

const EVENT_COLOR: Record<string, SemanticColor> = {
  // Agent lifecycle (includes deprecated events for historical stream rendering)
  'agent.started': 'success', // deprecated: no longer published
  'agent.stopped': 'neutral',
  'agent.killed': 'error',
  'agent.interrupted': 'warning',
  'agent.error': 'warning',
  'agent.complete': 'success',
  'agent.name': 'sky', // deprecated: no longer published
  'agent.setup': 'cyan',
  'agent.plan': 'info',
  'agent.progress': 'sky',
  'agent.need_input': 'warning',
  'agent.tool_use': 'neutral',
  'agent.dev_server': 'teal',
  'agent.pr_created': 'indigo',
  'agent.subagent': 'violet',
  'agent.compact': 'neutral',
  'agent.status_change': 'neutral', // deprecated: no longer published
  // Agent sources
  'agent.source.dashboard': 'sky',
  'agent.source.sentry_issue': 'orange',
  'agent.source.linear_issue': 'indigo',
  'agent.source.slack': 'purple',
  'agent.source.flow': 'indigo',
  'agent.flow_result': 'teal',
  // Messages
  message: 'violet',
  webhook: 'info',
  // Runloop turn protocol
  'turn.started': 'cyan',
  'turn.message_chunk': 'cyan',
  'turn.tool_call': 'cyan',
  'turn.tool_call_update': 'cyan',
  'turn.completed': 'cyan',
  'turn.cancelled': 'neutral',
  'turn.failed': 'error',
  // Claude CLI raw protocol (unprefixed)
  system: 'indigo',
  user: 'sky',
  assistant: 'violet',
  result: 'success',
  error: 'error',
  rate_limit_event: 'warning',
  control_request: 'warning',
  control_response: 'success',
  // Flow events
  'flow.started': 'info',
  'flow.node_started': 'info',
  'flow.node_completed': 'success',
  'flow.node_error': 'error',
  'flow.approval_requested': 'warning',
  'flow.approval_responded': 'success',
  'flow.completed': 'success',
  'flow.error': 'error',
  'flow.cancelled': 'neutral',
};

export const EVENT_BADGE_CLASS: Record<string, string> = Object.fromEntries(
  Object.entries(EVENT_COLOR).map(([k, v]) => [k, badgeClass[v]]),
);

export const EVENT_DOT_CLASS: Record<string, string> = Object.fromEntries(
  Object.entries(EVENT_COLOR).map(([k, v]) => [k, dotClass[v]]),
);

export const DEFAULT_EVENT_BADGE_CLASS = badgeClass.neutral;
export const DEFAULT_EVENT_DOT_CLASS = dotClass.neutral;

// ---------------------------------------------------------------------------
// Integration Run Status (PR Review, Linear, Sentry)
// ---------------------------------------------------------------------------

export const INTEGRATION_RUN_STATUS: Record<
  string,
  { dot: string; badge: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  running: { dot: `${dotClass.info} animate-pulse`, badge: 'default' },
  waiting_approval: { dot: `${dotClass.warning} animate-pulse`, badge: 'default' },
  completed: { dot: dotClass.success, badge: 'secondary' },
  error: { dot: dotClass.error, badge: 'destructive' },
  cancelled: { dot: dotClass.neutral, badge: 'outline' },
};

export function getIntegrationRunStatus(status: string) {
  return (
    INTEGRATION_RUN_STATUS[status] ?? { dot: 'bg-muted-foreground', badge: 'outline' as const }
  );
}
